import { Container, Flex, Group, Popover } from "@mantine/core";
import { type CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import { useEffect, useState } from "react";

import { useActiveTask, useAgentSdk, type VoiceTask } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { type Contact, TFN_LENGTH, US } from "@/shared-types";
import { extractNumbers, isInternalTfn } from "@/shared-utils";

import { AddParticipantButton } from "../CallControls/Buttons/AddParticipantButton";
import { useControlsStore } from "../CallControls/controls.store";
import { PhoneNumberInput } from "./components/PhoneNumberInput";
import { SegmentedTransferInput } from "./components/SegmentedTransferInput/SegmentedTransferInput";
import { TransferCallButton } from "./components/TransferCallButton";
import styles from "./TransferCall.module.css";
import { useContactsStateStore } from "./vdnContacts.store";

interface TransferCallProps {
    voiceTask: VoiceTask;
    countrySelectEnabled: boolean;
    clientEnabledCountries: CountryCode[];
    isVdnTransferInputEnabled: boolean;
    defaultCountry: CountryCode;
    isSegmentedTransferMenuEnabled: boolean;
    isInternationalPhoneEnabled: boolean;
}

export function TransferCall({
    voiceTask,
    countrySelectEnabled,
    clientEnabledCountries,
    isVdnTransferInputEnabled,
    defaultCountry,
    isSegmentedTransferMenuEnabled,
}: TransferCallProps) {
    const logger = useLogger({ module: "TransferCallPanel" });
    const agentSdk = useAgentSdk();
    const task = useActiveTask();
    const [transferTfn, setTransferTfn] = useState<string>();
    const [transferVdn, setTransferVdn] = useState<Contact>();

    const controlLock = useControlsStore((state) => state.controlLock);

    useEffect(() => {
        if (!task) return;
        void (async () => {
            try {
                await agentSdk.fetchContactsForTask(task);
            } catch (error) {
                logger.warn({ error }, "fetchContactsForTask failed.");
            }
        })();
        // oxlint-disable-next-line exhaustive-deps
    }, [agentSdk]);

    const contacts = useContactsStateStore((state) => state.contacts);
    const isAddButtonDisabled = (!transferTfn && !transferVdn) || controlLock;

    const trySetTransferTfn = (tfn: string | undefined) => {
        if (!tfn) {
            setTransferTfn(undefined);
            return;
        }

        const digitsOnly = extractNumbers(tfn).slice(-TFN_LENGTH); // Prevent adding any numbers from the name
        const parsed = parsePhoneNumberFromString(`${US}${digitsOnly}`);

        if (!parsed?.isValid() && !isInternalTfn(tfn, contacts)) {
            //TODO: should we reset state and handle this error more gracefully?
            throw new Error("Invalid phone number");
        }

        setTransferTfn(digitsOnly);
        setTransferVdn(undefined);
    };

    const trySetTransferVdn = (vdn: Contact | undefined) => {
        setTransferVdn(vdn);

        if (vdn) {
            setTransferTfn(undefined);
        }
    };

    return (
        <Popover zIndex="var(--mantine-priority-highest)">
            <Container bg="var(--exwo-color-control-bar-bg)" classNames={{ root: styles.containerRoot }}>
                {isSegmentedTransferMenuEnabled ? (
                    <SegmentedTransferInput
                        setTransferTfn={setTransferTfn}
                        setTransferVdn={setTransferVdn}
                        countrySelectEnabled={countrySelectEnabled}
                        clientEnabledCountries={clientEnabledCountries}
                        isVdnTransferInputEnabled={isVdnTransferInputEnabled}
                        defaultCountry={defaultCountry}
                    />
                ) : (
                    <PhoneNumberInput
                        transferTfn={transferTfn}
                        setTransferTfn={trySetTransferTfn}
                        transferVdn={transferVdn}
                        setTransferVdn={trySetTransferVdn}
                    />
                )}
                <Flex align="center" justify="end">
                    <Group>
                        <TransferCallButton task={voiceTask} transferTfn={transferTfn} transferVdn={transferVdn} />
                        <AddParticipantButton
                            task={voiceTask}
                            transferVdn={transferVdn}
                            transferTfn={transferTfn}
                            disabled={isAddButtonDisabled}
                            isPrimary={
                                voiceTask.customerLeftTheConference &&
                                !!transferTfn &&
                                voiceTask.callerId.includes(transferTfn)
                            }
                        />
                    </Group>
                </Flex>
            </Container>
        </Popover>
    );
}
